import { useCallback } from "react";
import { ItemType } from "@thenounproject/lingo-core";

import useNotifications from "@actions/useNotifications";
import useShowModal, { ModalTypes } from "@redux/actions/useModals";
import useCreateFileAssets from "@redux/actions/items/useCreateFileAssets";
import useBatchCreateColorAssets from "@redux/actions/items/useBatchCreateColorAssets";

import type { InsertPosition } from "@redux/actions/uploads";

import type {
  UseAssetPasteOptions,
  PasteResult,
  PasteInsertConfig,
  DetectedPasteData,
} from "../types";
import { usePasteDetection } from "./usePasteDetection";

/**
 * Computes the insert position based on context configuration.
 *
 * For gallery contexts (with itemId), creates gallery-level position.
 * For section-based contexts (with items/selectedItems), computes relative position.
 * For space-only contexts (like the library), returns simple space-level position.
 *
 * @example
 * // Gallery context (e.g., ManualGallery) - assets associated with a gallery item
 * const { pasteHandler } = useAssetPaste({
 *   insertConfig: {
 *     spaceId: space.id,
 *     kitId: kit.kitId,
 *     itemId: item.id,
 *   },
 * });
 *
 * usePaste(pasteHandler, isModalOpen || !canEditContent);
 */
function computeInsertPosition(config: PasteInsertConfig): InsertPosition {
  const { spaceId, kitId, sectionId, itemId, items, selectedItems } = config;

  // Gallery context (e.g., ManualGallery) - assets associated with a gallery item
  if (itemId && kitId) {
    return {
      itemId,
      kitId,
      spaceId,
    };
  }

  // Space-only context (e.g., AssetLibrary)
  if (!kitId || !sectionId) {
    return { spaceId };
  }

  // Section-based context (Kit section / GalleryItems.tsx)
  const insertPosition: InsertPosition = {
    displayOrder: "append",
    insertIndex: items?.length ? items.length - 1 : 0,
    kitId,
    sectionId,
    spaceId,
  };

  // Adjust for selected items - insert after selection
  if (selectedItems?.length && items?.length) {
    const lastSelectedVersionedId = selectedItems[selectedItems.length - 1];
    // Extract item ID from versioned ID format (e.g., "item-uuid-0" -> "item-uuid"). displayOrder expects unversioned ID.
    const lastSelectedItemId = lastSelectedVersionedId.substring(
      0,
      lastSelectedVersionedId.lastIndexOf("-")
    );
    const selectedIndex = items.findIndex(item => `${item.id}-0` === lastSelectedVersionedId);

    insertPosition.displayOrder = `after:${lastSelectedItemId}`;
    insertPosition.insertIndex = selectedIndex + 1;

    // For batch inserts, provide nextItemId so generateInsertPositionForUploads
    // can convert "after:X" to "before:nextItem" for correct positioning
    if (selectedIndex >= 0 && selectedIndex < items.length - 1) {
      insertPosition.nextItemId = items[selectedIndex + 1].id;
    }
  }

  return insertPosition;
}

/**
 * Hook for handling asset paste operations (colors, Figma links, SVG, images).
 * Automatically creates assets when paste is detected.
 *
 * This is a high-level hook that wraps usePasteDetection and adds automatic asset creation.
 * For manual control over paste handling, use usePasteDetection directly.
 *
 * Returns a paste handler compatible with usePaste from @thenounproject/lingo-core.
 * We are responsible for calling usePaste with the returned handler
 * and our own skip condition.
 *
 * @example
 * // Kit section context (GalleryItems)
 * const { pasteHandler } = useAssetPaste({
 *   insertConfig: {
 *     spaceId: space.id,
 *     kitId: section?.kitId,
 *     sectionId: section.id,
 *     items,
 *     selectedItems,
 *   },
 * });
 * usePaste(pasteHandler, isModalOpen || !canEditContent);
 */
export function useAssetPaste(options: UseAssetPasteOptions) {
  const { insertConfig, features = {}, callbacks = {} } = options;

  const [createFileAssets] = useCreateFileAssets();
  const [batchCreateColorAssets] = useBatchCreateColorAssets();
  const { showNotification } = useNotifications();
  const { showModal } = useShowModal();

  /**
   * Handle detected paste data by creating appropriate assets.
   */
  const handleDetectedPaste = useCallback(
    async (data: DetectedPasteData) => {
      const insertPosition = computeInsertPosition(insertConfig);

      const notifyResult = (result: PasteResult) => {
        (result.success ? callbacks.onPasteSuccess : callbacks.onPasteError)?.(result);
      };

      let pasteType: PasteResult["type"] = "color";

      try {
        if (data.type === "color") {
          pasteType = "color";
          await batchCreateColorAssets({
            colors: data.colors,
            insertPosition,
          });
          notifyResult({ type: "color", success: true });
        } else if (data.type === "figma") {
          pasteType = "figma";
          // Figma paste requires section context
          if (!insertConfig.sectionId) {
            notifyResult({
              type: "figma",
              success: false,
              error: "Figma import requires a section context",
            });
            return;
          }

          showModal(ModalTypes.FIGMA_LINK_MODAL, {
            insertPosition,
            preloadedState: { url: data.url },
          });
          notifyResult({ type: "figma", success: true });
        } else if (data.type === "svg" || data.type === "image" || data.type === "pdf") {
          pasteType = data.type;
          const result = await createFileAssets({
            files: [data.file],
            itemType: insertConfig.itemType || ItemType.asset,
            insertPosition,
            displayProperties: insertConfig.displayProperties,
            previewBackground: insertConfig.previewBackground,
          });

          if (result.error) {
            notifyResult({ type: pasteType, success: false, error: result.error.message });
          } else {
            notifyResult({ type: pasteType, success: true });
          }
        }
      } catch (err) {
        console.error(`Error creating asset from clipboard:`, err);
        showNotification({
          message: `Failed to create asset from clipboard`,
          level: "error",
        });
        notifyResult({ type: pasteType, success: false, error: String(err) });
      }
    },
    [insertConfig, createFileAssets, batchCreateColorAssets, showModal, showNotification, callbacks]
  );

  // Use the low-level detection hook and handle detected data by creating assets
  const { pasteHandler } = usePasteDetection({
    features,
    onDetected: handleDetectedPaste,
  });

  return {
    /** Paste handler for use with usePaste hook */
    pasteHandler,
  };
}
